import React from "react";

import { HACKPAL_ICON } from "./assetMap";
import {
  ExcitementIcon,
  ForestIcon,
  InAweIcon,
  InstrumentIcon,
  LandscapeIcon,
  MountainsIcon,
  MusicIcon,
  NighttimeIcon,
  PlantIcon,
  PortraitIcon,
  SnoozeIcon,
  SparklesIcon,
  SquareIcon,
  UsualIcon,
  WrenchIcon,
} from "./optionIcons";
import {
  Accessory,
  Backdrop,
  CanvasOrientation,
  Effect,
  Feeling,
  GraphicCustomization,
  Hackpal,
  HeaderTextOption,
  SignOption,
} from "./types";

export const HEADER_TEXT_COPY: Record<HeaderTextOption, string> = {
  [HeaderTextOption.IM_GOING]: "I'm going to Hack the North!",
  [HeaderTextOption.SEE_YOU]: "See you at Hack the North!",
};

/**
 * Always two lines: lead-in on line 1, "Hack the North!" on line 2.
 */
export const HEADER_TEXT_LINES: Record<HeaderTextOption, string[]> = {
  [HeaderTextOption.IM_GOING]: ["I'm going to", "Hack the North!"],
  [HeaderTextOption.SEE_YOU]: ["See you at", "Hack the North!"],
};

export const BUILDING_TEXT_MAX_LENGTH = 30;
export const STAMP_NAME_MAX_LENGTH = 20;
export const DREAM_BIG_SIGN = "Dream big and build!";
export const LOOKING_FOR_TEAM_SIGN = "I'm looking for a team!";

export const resolveSignText = (
  customization: Pick<GraphicCustomization, "signOption" | "buildingText">
): string => {
  switch (customization.signOption) {
    case SignOption.DREAM_BIG:
      return DREAM_BIG_SIGN;
    case SignOption.LOOKING_FOR_TEAM:
      return LOOKING_FOR_TEAM_SIGN;
    case SignOption.BUILDING: {
      const suffix = customization.buildingText.trim();
      return suffix ? `I'm building ${suffix}` : "I'm building...";
    }
    default:
      return DREAM_BIG_SIGN;
  }
};

export const DEFAULT_CUSTOMIZATION: GraphicCustomization = {
  orientation: CanvasOrientation.SQUARE,
  backdrop: Backdrop.NIGHTTIME,
  hackpal: Hackpal.GINNY,
  feeling: Feeling.DEFAULT,
  accessory: Accessory.INSTRUMENT,
  effect: Effect.MUSIC,
  headerText: HeaderTextOption.IM_GOING,
  signOption: SignOption.DREAM_BIG,
  buildingText: "",
  stampName: "",
};

const HackpalThumb: React.FC<{ pal: Hackpal }> = ({ pal }) => (
  <img
    src={HACKPAL_ICON[pal]}
    alt=""
    aria-hidden
    draggable={false}
    style={{ width: 24, height: 24, objectFit: "contain" }}
  />
);

/** A single selectable field option rendered as an OptionCard */
export interface OptionConfig {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export const SIGN_OPTIONS: OptionConfig[] = [
  {
    value: SignOption.DREAM_BIG,
    label: DREAM_BIG_SIGN,
  },
  {
    value: SignOption.LOOKING_FOR_TEAM,
    label: LOOKING_FOR_TEAM_SIGN,
  },
  {
    value: SignOption.BUILDING,
    label: "I'm building...",
  },
];

/** A titled row of options bound to one customization field */
export interface OptionGroupConfig {
  field: keyof Omit<GraphicCustomization, "buildingText" | "stampName">;
  label: string;
  options: OptionConfig[];
}

export interface OptionSectionConfig {
  title: string;
  groups: OptionGroupConfig[];
}

export const OPTION_SECTIONS: OptionSectionConfig[] = [
  {
    title: "Set the scene",
    groups: [
      {
        field: "orientation",
        label: "Start with a canvas",
        options: [
          {
            value: CanvasOrientation.SQUARE,
            label: "Square",
            icon: <SquareIcon />,
          },
          {
            value: CanvasOrientation.PORTRAIT,
            label: "Portrait",
            icon: <PortraitIcon />,
          },
          {
            value: CanvasOrientation.LANDSCAPE,
            label: "Landscape",
            icon: <LandscapeIcon />,
          },
        ],
      },
      {
        field: "backdrop",
        label: "Pick a backdrop",
        options: [
          {
            value: Backdrop.NIGHTTIME,
            label: "Nighttime",
            icon: <NighttimeIcon />,
          },
          { value: Backdrop.FOREST, label: "Forest", icon: <ForestIcon /> },
          {
            value: Backdrop.MOUNTAINS,
            label: "Mountains",
            icon: <MountainsIcon />,
          },
        ],
      },
    ],
  },
  {
    title: "Who's coming along?",
    groups: [
      {
        field: "hackpal",
        label: "Choose a Hackpal",
        options: [
          {
            value: Hackpal.GINNY,
            label: "Ginny",
            icon: <HackpalThumb pal={Hackpal.GINNY} />,
          },
          {
            value: Hackpal.PATCH,
            label: "Patch",
            icon: <HackpalThumb pal={Hackpal.PATCH} />,
          },
          {
            value: Hackpal.VINYL,
            label: "Vinyl",
            icon: <HackpalThumb pal={Hackpal.VINYL} />,
          },
        ],
      },
      {
        field: "feeling",
        label: "How are they feeling today?",
        options: [
          { value: Feeling.DEFAULT, label: "The usual", icon: <UsualIcon /> },
          { value: Feeling.SPARKLE, label: "In awe", icon: <InAweIcon /> },
          {
            value: Feeling.SNOOZE,
            label: "Ready for a nap",
            icon: <SnoozeIcon />,
          },
        ],
      },
    ],
  },
  {
    title: "Add the details",
    groups: [
      {
        field: "accessory",
        label: "Bring something along",
        options: [
          {
            value: Accessory.INSTRUMENT,
            label: "Instrument",
            icon: <InstrumentIcon />,
          },
          { value: Accessory.PLANT, label: "Plant", icon: <PlantIcon /> },
          { value: Accessory.WRENCH, label: "Wrench", icon: <WrenchIcon /> },
        ],
      },
      {
        field: "effect",
        label: "Fill the air with personality",
        options: [
          {
            value: Effect.EXCITEMENT,
            label: "Excitement",
            icon: <ExcitementIcon />,
          },
          {
            value: Effect.MUSIC,
            label: "Music",
            icon: <MusicIcon />,
          },
          {
            value: Effect.SPARKLES,
            label: "Sparkles",
            icon: <SparklesIcon />,
          },
        ],
      },
    ],
  },
];
